import React, { useEffect } from 'react';
import { Provider } from 'mobx-react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Amplify, Auth, Hub } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { Global, css } from '@emotion/react';
import { configure as AxiosHooksConfigure } from 'axios-hooks';
import { ConfigProvider as AntdProvider } from 'antd';
import api from '@lib/api';
import { useAuthStore, AuthState, FetchedUserData } from '@stores/auth';
import App from './App';

import RootStore from './stores';
import AmplifyConfig from './config/amplify';

import koKR from 'antd/lib/locale/ko_KR';

Amplify.configure(AmplifyConfig);

const Root: React.FC = () => {
  const { setAuthState, setAuthData, setUserData, setToken } = useAuthStore();

  useEffect(() => {
    let authInProgress = false;

    async function auth(user: CognitoUser) {
      authInProgress = true;
      const session = user.getSignInUserSession();

      if (!session) {
        setAuthState(AuthState.NOT_SIGNED_IN);
        Auth.signOut();

        // Modal.error

        return;
      }

      const idToken = session.getIdToken();
      const tokenPayload = idToken.decodePayload();

      api.defaults.headers.common['Authorization'] = `Bearer ${idToken.getJwtToken()}`;
      AxiosHooksConfigure({ axios: api });
      setAuthData(idToken.payload);
      setToken(idToken.getJwtToken());

      const fetchUserRes = await api.get<FetchedUserData>('/user', {
        params: {
          opt_fields: 'poseprocessingvideoids',
        },
      });

      setUserData({
        ...fetchUserRes.data,
        avatarUrl: fetchUserRes.data.avatarUrl ?? tokenPayload.picture,
        name: fetchUserRes.data.name ?? tokenPayload.name,
      });
    }

    Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;

      /**
       * event
       * - signIn
       * - signOut
       * - cognitoHostedUI
       * - tokenRefresh
       * - oAuthSignOut
       */

      switch (event) {
        case 'signIn':
          if (!authInProgress) auth(payload.data);
          break;
        case 'tokenRefresh':
        case 'cognitoHostedUI':
          break;
        case 'oAuthSignOut':
          setAuthState(AuthState.NOT_SIGNED_IN);
          setAuthData(null);
          setUserData(null);
          break;
        default:
          Auth.currentAuthenticatedUser()
            .then((user) => {
              if (!authInProgress) auth(user);
            })
            .catch(() => {
              //
            });
      }
    });

    Auth.currentAuthenticatedUser()
      .then((user) => {
        auth(user);
      })
      .catch((e: unknown) => {
        switch (e) {
          case 'The user is not authenticated':
            setAuthState(AuthState.NOT_SIGNED_IN);
            break;
          default:
            console.error(e);
          // [TODO] Modal.error
        }
      });
  }, [setAuthData, setAuthState, setUserData, setToken]);

  return (
    <Router>
      <Global styles={GlobalStyles} />
      <Provider locale={koKR} {...RootStore}>
        <AntdProvider>
          <App />
        </AntdProvider>
      </Provider>
    </Router>
  );
};

const GlobalStyles = css`
  /**
 * Copyright (c) 2015 Spoqa, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
 * LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
 * WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

  @font-face {
    font-family: 'Spoqa Han Sans';
    font-weight: 700;
    src: local('Spoqa Han Sans Bold'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansBold.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansBold.woff') format('woff'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansBold.ttf') format('truetype');
  }

  @font-face {
    font-family: 'Spoqa Han Sans';
    font-weight: 400;
    src: local('Spoqa Han Sans Regular'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansRegular.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansRegular.woff') format('woff'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansRegular.ttf') format('truetype');
  }

  @font-face {
    font-family: 'Spoqa Han Sans';
    font-weight: 300;
    src: local('Spoqa Han Sans Light'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansLight.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansLight.woff') format('woff'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansLight.ttf') format('truetype');
  }

  @font-face {
    font-family: 'Spoqa Han Sans';
    font-weight: 100;
    src: local('Spoqa Han Sans Thin'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansThin.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansThin.woff') format('woff'),
      url('//static.sparky.tv/fonts/SpoqaHanSans/SpoqaHanSansThin.ttf') format('truetype');
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Thin.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Thin.woff') format('woff');
    font-weight: 200;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Light.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Light.woff') format('woff');
    font-weight: 300;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-DemiLight.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-DemiLight.woff') format('woff');
    font-weight: 400;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Regular.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Regular.woff') format('woff');
    font-weight: 500;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Medium.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Medium.woff') format('woff');
    font-weight: 600;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Bold.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Bold.woff') format('woff');
    font-weight: 700;
  }

  @font-face {
    font-family: 'Noto Sans CJK KR';
    src: local('Noto Sans CJK KR'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Black.woff2') format('woff2'),
      url('//static.sparky.tv/fonts/Noto_Sans_CJK_KR/NotoSansKR-Black.woff') format('woff');
    font-weight: 800;
  }

  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu72xKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
  }
  /* cyrillic */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu5mxKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
  }
  /* greek-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu7mxKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+1F00-1FFF;
  }
  /* greek */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4WxKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+0370-03FF;
  }
  /* vietnamese */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu7WxKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0,
      U+1EA0-1EF9, U+20AB;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu7GxKKTU1Kvnz.woff2') format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113,
      U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: local('Roboto'), local('Roboto-Regular'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,
      U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  /* cyrillic-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fCRc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
  }
  /* cyrillic */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fABc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
  }
  /* greek-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fCBc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+1F00-1FFF;
  }
  /* greek */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fBxc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0370-03FF;
  }
  /* vietnamese */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fCxc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0,
      U+1EA0-1EF9, U+20AB;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fChc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113,
      U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: local('Roboto Medium'), local('Roboto-Medium'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fBBc4AMP6lQ.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,
      U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  /* cyrillic-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfCRc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
  }
  /* cyrillic */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfABc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
  }
  /* greek-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfCBc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+1F00-1FFF;
  }
  /* greek */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBxc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0370-03FF;
  }
  /* vietnamese */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfCxc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0,
      U+1EA0-1EF9, U+20AB;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfChc4AMP6lbBP.woff2') format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113,
      U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: local('Roboto Bold'), local('Roboto-Bold'),
      url('//fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc4AMP6lQ.woff2') format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,
      U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }

  html,
  body {
    width: 100%;
    height: 100%;
  }

  :lang(en) {
    font-family: 'Spoqa Han Sans', 'Apple SD Gothic Neo', arial, sans-serif;
  }

  :lang(ko) {
    font-family: 'Spoqa Han Sans', 'Apple SD Gothic Neo', arial, sans-serif;
  }

  :lang(ja) {
    font-family: 'Spoqa Han Sans', 'Apple SD Gothic Neo', arial, sans-serif;
  }

  // font-family: 'Noto Sans CJK KR', 'Apple SD Gothic Neo', arial, sans-serif;

  html {
    overflow-x: auto;

    &.is-mobile {
      overflow: hidden;

      body {
        min-width: auto;
      }
    }
  }

  body {
    overflow: hidden;
    min-width: 1280px;
    font-family: 'Spoqa Han Sans', 'Sans-serif';
  }

  #root {
    height: 100%;
    min-width: 320px;

    & > div,
    & > .ant-layout,
    & > div > .ant-layout {
      height: 100%;
    }
  }

  .sr-only {
    overflow: hidden;
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    clip: rect(0 0 0 0);
  }

  .global-loading-dimmer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    background-color: #fcfcfc;
    opacity: 0.8;
    animation: fadeIn 75ms ease;
  }

  button {
    border: none;
    margin: 0;
    padding: 0;
    width: auto;
    overflow: visible;

    background: transparent;

    /* inherit font & color from ancestor */
    color: inherit;
    font: inherit;

    /* Normalize 'line-height'. Cannot be changed from 'normal' in Firefox 4+. */
    line-height: normal;

    /* Corrects font smoothing for webkit */
    -webkit-font-smoothing: inherit;
    -moz-osx-font-smoothing: inherit;

    /* Corrects inability to style clickable 'input' types in iOS */
    -webkit-appearance: none;

    &::-moz-focus-inner {
      border: 0;
      padding: 0;
    }
  }

  /* Antd Menu override */
  .ant-menu {
    box-shadow: none;
  }

  /* Antd Select customization */
  .ant-select-item.ant-select-item-option {
    &.ant-select-item-option-disabled {
      .select-published-icon {
        svg path {
          fill: #c2cad4;
        }
      }
    }

    .select-published-icon {
      display: inline-block;
      position: relative;
      top: 2px;
      margin-right: 8px;
      vertical-align: top;
    }

    .select-published-text {
      font-size: 14px;
      line-height: 1.3;
    }
  }

  /* Antd Tooltip customization */
  .ant-tooltip {
    &.text-dark {
      .ant-tooltip-inner {
        color: #090909 !important;
      }
    }

    &.align-center {
      .ant-tooltip-inner {
        text-align: center;
      }
    }

    &.font-size-12 {
      .ant-tooltip-inner {
        font-size: 12px;
      }
    }

    &.font-size-15 {
      .ant-tooltip-inner {
        font-size: 15px;
      }
    }
  }

  .sider-transition-enter {
    position: absolute;
    opacity: 0;
  }
  .sider-transition-enter-active {
    opacity: 1;
    transition: opacity 200ms;
  }
  .sider-transition-exit {
    position: absolute;
    opacity: 1;
  }
  .sider-transition-exit-active {
    opacity: 0;
    transition: opacity 200ms;
  }

  .header-progress-item-transition-enter {
    transform: scale(1.5);
    opacity: 0;
  }
  .header-progress-item-transition-enter-active {
    transform: scale(1);
    opacity: 1;
    transition: all 300ms;
  }
  .header-progress-item-transition-exit {
    transform: scale(1);
    opacity: 1;
  }
  .header-progress-item-transition-exit-active {
    transform: scale(1.5);
    opacity: 0;
    transition: all 300ms;
  }

  .fade-transition-enter {
    opacity: 0;
  }
  .fade-transition-enter-active {
    opacity: 1;
    transition: opacity 200ms;
  }
  .fade-transition-exit {
    opacity: 1;
  }
  .fade-transition-exit-active {
    opacity: 0;
    transition: opacity 200ms;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  html {
    .ant-notification {
      .ant-notification-notice-content {
        .ant-notification-notice-message {
          margin-left: 36px;
          font-weight: bold;
          font-size: 15px;
          line-height: 1.3;
          color: #334d6e;
        }

        .ant-notification-notice-description {
          overflow: hidden;
          margin: 14px 0 0 0;
          color: #334d6e;
          white-space: pre-wrap;
          word-break: break-all;
        }

        .ant-notification-notice-btn {
          float: left;

          button {
            padding: 6.4px 32px;
            font-size: 14px;
          }
        }
      }
    }

    .ant-btn {
      &.custom-disabled-style:disabled {
        border-color: #646565 !important;
        background-color: #646565 !important;
        color: #acadae;
      }
    }

    .ant-upload-block {
      display: block;

      .ant-upload.ant-upload-select.ant-upload-select-text {
        display: block;

        .ant-upload {
          display: block;
          padding: 5px 12px;
        }
      }
    }

    .ant-dropdown-menu {
      li.ant-dropdown-menu-item.ant-dropdown-menu-item-active {
        background: #334d6e;
        color: #fff;

        .ant-upload span {
          color: #fff;
        }
      }
    }
  }
`;

export default Root;
